import classNames from 'classnames';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { FC, Ref, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { availabilityStoreHandler } from '../../../pages/Timetable/Plan/TeachingBlockVersion/TeachingBlockVersion';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { GridCard } from '../GridCard/GridCard';
import { GridDateType, RowItem } from '../types';
import { VirtualGridCard } from '../VirtualGridCard/VirtualGridCard';
import styles from './GridWeek.module.scss';

type GridWeekProps = {
  row: RowItem;
  weekNumber: number;
  year: number;
  days: GridDateType[];
  weekRef?: Ref<HTMLDivElement>;
};

type DayAvailability = GridDateType & {
  valid: boolean;
  blocked: boolean;
  maybe: boolean;
  placed: boolean;
};

export const GridWeek: FC<GridWeekProps> = observer(({ row, weekNumber, year, days, weekRef }) => {
  const { teachingBlockVersionUuid } = useParams();

  const store = useTeachingBlockStore();

  const availabilityStore = availabilityStoreHandler.get(teachingBlockVersionUuid ?? '');

  const [availabilityDays, setAvailabilityDays] = useState<DayAvailability[]>(
    days.map((d) => ({ ...d, valid: false, blocked: false, maybe: false, placed: false })),
  );

  const virtualCardsInWeek = store.virtualCards.filter((card) => {
    const parent = store.getParentCard(card.parentUuid);
    if (!parent) throw new Error('parent is undefined');
    return (
      store.isUuidPresentInTimetableCard(parent, row.value) &&
      dayjs(card.startDateString).utc().week() === weekNumber &&
      dayjs(card.startDateString).utc().year() === year &&
      store.pickedCard?.card.uuid !== card.parentUuid
    );
  });

  const placedCardsInWeek = store.placedCards.filter((card) => {
    return (
      store.isUuidPresentInTimetableCard(card, row.value) &&
      dayjs(card.startDate).utc().week() === weekNumber &&
      dayjs(card.startDate).utc().year() === year &&
      store.pickedCard?.card.uuid !== card.uuid
    );
  });

  useEffect(() => {
    if (store.pickedCard && availabilityStore) {
      const availabilityDays: DayAvailability[] = [];
      const startDay = dayjs(days[0].dateString).startOf('day').utc();
      const endDay = dayjs(days[6].dateString).startOf('day').utc();
      const allDays = endDay.diff(startDay, 'days');
      for (let i = 0; i <= allDays; i++) {
        if (store.isUuidPresentInTimetableCard(store.pickedCard.card, row.value)) {
          const availabilityCard = store.getAvailabilityCardForPickedCard();
          if (availabilityCard) {
            const { status } = availabilityStore.checkCardAvailability(
              startDay.add(i, 'days').toDate(),
              availabilityCard,
              'classes',
            );
            availabilityDays.push({
              ...days[i],
              valid: status !== 'blocked' && status !== 'maybe' && status !== 'placed',
              blocked: status === 'blocked',
              maybe: status === 'maybe',
              placed: status === 'placed',
            });
            setAvailabilityDays(availabilityDays);
          }
        }
      }
    } else {
      setAvailabilityDays(days.map((d) => ({ ...d, valid: false, blocked: false, maybe: false, placed: false })));
    }
  }, [availabilityStore, days, store.isUuidPresentInTimetableCard, store.pickedCard, row.value, store]);

  return (
    <div ref={weekRef} className={styles['grid-week']}>
      {availabilityDays.map(({ valid, blocked, maybe, placed, holidayUuid, dateString }, dayIndex) => {
        const dayClasses = classNames('droppable', styles.day, {
          [styles.valid]: valid,
          [styles.blocked]: blocked,
          [styles.maybe]: maybe,
          [styles.placed]: placed,
          [styles.holiday]: !!holidayUuid,
        });

        return (
          <div
            key={weekNumber + '_' + dayIndex}
            style={{ left: `calc(100% / 7 * ${dayIndex})` }}
            className={dayClasses}
            onMouseDown={(event) => {
              let firstDroppable = days[0];
              for (let i = 0; i < dayIndex; i++) {
                if (
                  days[i].holidayUuid ||
                  (store.currentVersion.active && dayjs(days[i].dateString).isSameOrBefore(dayjs().startOf('day')))
                ) {
                  firstDroppable = days[i + 1] ? days[i + 1] : days[0];
                }
              }
              if (!holidayUuid && store.pickedCard) {
                store.placeCard(store.pickedCard.card, firstDroppable?.dateString ?? dateString, {
                  x: event.pageX,
                  y: event.pageY,
                });
              }
            }}
          ></div>
        );
      })}

      {virtualCardsInWeek.map((card) => {
        return <VirtualGridCard key={card.uuid} card={card} row={row} />;
      })}
      {placedCardsInWeek.map((card, i) => {
        let duration = (card.durationInDays ?? 7) / 7;
        if (duration < 0) duration = 0;
        let offset = dayjs(card.startDate).day() - 1;
        if (offset < 0) offset = 6;
        const borders = (duration - 1) * 2;

        const gridCard =
          store.gridContext === 'classes'
            ? card.classGridCards.find((c) => c.classUuid === row.value)
            : store.gridContext === 'teachers'
              ? card.teacherGridCards.find((t) => t.teacherUuid === row.value)
              : store.gridContext === 'rooms'
                ? card.roomGridCards.find((r) => r.roomUuid === row.value)
                : null;

        return (
          <GridCard
            key={card.uuid + i}
            card={card}
            onCardClick={(event) => store.pickCard(card, { x: event.pageX, y: event.pageY })}
            background={gridCard?.cssGradientString ?? ''}
            textColor={gridCard?.textColor ?? ''}
            left={`${(offset / 7) * 100}%`}
            top={gridCard?.cssTop ?? '0%'}
            width={`calc(${duration * 100}% + ${borders}px)`}
            height={gridCard?.cssHeight ?? '100%'}
          />
        );
      })}
    </div>
  );
});
